import { useState } from "react"
import {
    Typography,
    Button,
    Space,
    Card,
    Select,
    Checkbox,
    Table,
    Input,
    message,
    Modal,
    Form,
    Tag,
    Statistic,
    Progress,
} from "antd"
import {
    PlusOutlined,
} from "@ant-design/icons"
import moment from "moment"

const { Title, Text } = Typography
const { Option } = Select

// Helper for Ant Design message
const showMessage = (type, content) => {
    message[type](content)
}

const APIWebhooks = () => {
    const [apiKeys, setApiKeys] = useState([
        { key: "1", name: "Internal App", created: "May 15, 2025", lastUsed: "June 13, 2025", status: "active" },
        { key: "2", name: "Reporting Tool", created: "April 1, 2025", lastUsed: "June 10, 2025", status: "active" },
    ])
    const [webhooks, setWebhooks] = useState([
        {
            key: "1",
            url: "https://example.com/hook",
            event: "On Execution Start",
            lastSuccess: "2 mins ago",
            status: "success",
        },
        { key: "2", url: "https://another.com/alert", event: "On Signal Detected", lastSuccess: "Never", status: "error" },
    ])
    const [isAddApiKeyModalVisible, setIsAddApiKeyModalVisible] = useState(false)
    const [isAddWebhookModalVisible, setIsAddWebhookModalVisible] = useState(false)
    const [apiKeyForm] = Form.useForm()
    const [webhookForm] = Form.useForm()

    const handleGenerateApiKey = (values) => {
        const newKey = {
            key: String(apiKeys.length + 1),
            name: values.name,
            created: moment().format("MMMM D, YYYY"),
            lastUsed: "Never",
            status: "active",
            // In a real app, a random key string would be generated here
        }
        setApiKeys([...apiKeys, newKey])
        setIsAddApiKeyModalVisible(false)
        apiKeyForm.resetFields()
        showMessage("success", "New API Key generated.")
    }

    const handleRevokeApiKey = (key) => {
        Modal.confirm({
            title: "Confirm Revoke",
            content: "Are you sure you want to revoke this API key?",
            onOk: () => {
                setApiKeys(apiKeys.filter((k) => k.key !== key))
                showMessage("success", "API Key revoked.")
            },
        })
    }

    const handleAddWebhook = (values) => {
        const newWebhook = {
            key: String(webhooks.length + 1),
            url: values.url,
            event: values.event,
            lastSuccess: "Never",
            status: "pending",
        }
        setWebhooks([...webhooks, newWebhook])
        setIsAddWebhookModalVisible(false)
        webhookForm.resetFields()
        showMessage("success", "Webhook added.")
    }

    const apiKeyColumns = [
        { title: "Key Name", dataIndex: "name", key: "name" },
        { title: "Created On", dataIndex: "created", key: "created" },
        { title: "Last Used", dataIndex: "lastUsed", key: "lastUsed" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => <Tag color={text === "active" ? "green" : "red"}>{text}</Tag>,
        },
        {
            title: "Actions",
            key: "actions",
            render: (text, record) => (
                <Space size="middle">
                    <Button type="link" onClick={() => showMessage("info", `Viewing key for ${record.name}`)}>
                        View
                    </Button>
                    <Button type="link" danger onClick={() => handleRevokeApiKey(record.key)}>
                        Revoke
                    </Button>
                </Space>
            ),
        },
    ]

    const webhookColumns = [
        { title: "Endpoint URL", dataIndex: "url", key: "url" },
        { title: "Event Trigger", dataIndex: "event", key: "event" },
        { title: "Last Success", dataIndex: "lastSuccess", key: "lastSuccess" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => {
                let color = "blue"
                if (text === "success") color = "green"
                if (text === "error") color = "red"
                return <Tag color={color}>{text}</Tag>
            },
        },
    ]

    const webhookLogs = [
        { key: "1", time: "10:32 AM", endpoint: "example.com/hook", status: "200 OK", responseTime: "138ms" },
        { key: "2", time: "10:29 AM", endpoint: "example.com/hook", status: "500 Timeout", responseTime: "N/A" },
    ]

    const webhookLogColumns = [
        { title: "Time", dataIndex: "time", key: "time" },
        { title: "Endpoint", dataIndex: "endpoint", key: "endpoint" },
        { title: "Status", dataIndex: "status", key: "status" },
        { title: "Response", dataIndex: "responseTime", key: "responseTime" },
    ]

    const [isWebhookLogsModalVisible, setIsWebhookLogsModalVisible] = useState(false)

    return (
        <Card className="mb-6">
            <div className="mb-4">
                <Title level={4}>API & Webhooks</Title>
                <Text type="secondary">Manage developer access and system-level integrations.</Text>
            </div>

            <Title level={5} className="mb-4">
                API Key Management
            </Title>
            <Table dataSource={apiKeys} columns={apiKeyColumns} pagination={false} className="mb-4" />
            <Button type="primary" onClick={() => setIsAddApiKeyModalVisible(true)} icon={<PlusOutlined />}>
                Generate New API Key
            </Button>

            <Modal
                title="Generate New API Key"
                visible={isAddApiKeyModalVisible}
                onCancel={() => setIsAddApiKeyModalVisible(false)}
                footer={null}
            >
                <Form form={apiKeyForm} onFinish={handleGenerateApiKey} layout="vertical">
                    <Form.Item name="name" label="Key Name" rules={[{ required: true, message: "Please enter a key name!" }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="rolePermissions" label="Role Permissions" initialValue="read_write">
                        <Select>
                            <Option value="read_write">Read/Write</Option>
                            <Option value="read_only">Read-only</Option>
                        </Select>
                    </Form.Item>
                    <Button type="primary" htmlType="submit">
                        Generate
                    </Button>
                </Form>
            </Modal>

            <Title level={5} className="mt-6 mb-4">
                Webhook Configuration
            </Title>
            <Table dataSource={webhooks} columns={webhookColumns} pagination={false} className="mb-4" />
            <Button type="primary" onClick={() => setIsAddWebhookModalVisible(true)} icon={<PlusOutlined />}>
                Add Webhook
            </Button>

            <Modal
                title="Add Webhook"
                visible={isAddWebhookModalVisible}
                onCancel={() => setIsAddWebhookModalVisible(false)}
                footer={null}
            >
                <Form form={webhookForm} onFinish={handleAddWebhook} layout="vertical">
                    <Form.Item
                        name="url"
                        label="Endpoint URL"
                        rules={[{ required: true, type: "url", message: "Please enter a valid URL!" }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="event"
                        label="Event Trigger"
                        rules={[{ required: true, message: "Please select an event!" }]}
                    >
                        <Select>
                            <Option value="On Execution Start">On Execution Start</Option>
                            <Option value="On Signal Detected">On Signal Detected</Option>
                            <Option value="On Settings Change">On Settings Change</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="secretKey" label="Secret Key (optional)">
                        <Input.Password />
                    </Form.Item>
                    <Form.Item name="enableRetries" valuePropName="checked" initialValue={true}>
                        <Checkbox>Enable retries</Checkbox>
                    </Form.Item>
                    <Button type="primary" htmlType="submit">
                        Add Webhook
                    </Button>
                </Form>
            </Modal>

            <Title level={5} className="mt-6 mb-4">
                Webhook Logs
            </Title>
            <Button onClick={() => setIsWebhookLogsModalVisible(true)}>View Webhook Logs</Button>
            <Modal
                title="Webhook Logs"
                visible={isWebhookLogsModalVisible}
                onCancel={() => setIsWebhookLogsModalVisible(false)}
                width={800}
                footer={null}
            >
                <Table dataSource={webhookLogs} columns={webhookLogColumns} pagination={{ pageSize: 5 }} />
            </Modal>

            <Title level={5} className="mt-6 mb-4">
                Rate Limit + Usage Stats
            </Title>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                    <Statistic title="Monthly API Calls" value={12843} suffix="/ 100,000" />
                    <Progress percent={12.84} showInfo={false} />
                </Card>
                <Card>
                    <Statistic title="Avg. Latency" value={420} suffix="ms" />
                </Card>
            </div>
        </Card>
    )
}

export default APIWebhooks;